import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import type { DNSValidationResponse } from '@/types/domain';

export interface DnsValidationOutcome {
  domainName: string;
  result: DNSValidationResponse;
}

export interface DnsValidationModalProps {
  /** Outcome to show; the modal is open while it is set */
  outcome: DnsValidationOutcome | null;
  onClose: () => void;
}

/** A record the domain resolves to, checked against the nodes' addresses */
interface CheckedRecord {
  value: string;
  type: 'A' | 'AAAA';
  matches: boolean;
}

/**
 * Node addresses by family and the records found, each checked against the
 * addresses of its family (addresses are compared in the server's canonical form).
 */
function checkRecords(result: DNSValidationResponse | undefined) {
  if (!result) return { ipv4: [], ipv6: [], records: [] };
  // Servers before IPv6 support only report expected_ip
  const ipv4 = result.expected_ips ?? (result.expected_ip && !result.expected_ip.includes(':') ? [result.expected_ip] : []);
  const ipv6 = result.ipv6_addresses ?? (result.expected_ip?.includes(':') ? [result.expected_ip] : []);
  const records: CheckedRecord[] = (result.dns_records ?? []).map((value) => {
    const type = value.includes(':') ? 'AAAA' : 'A';
    return { value, type, matches: (type === 'A' ? ipv4 : ipv6).includes(value) };
  });
  return { ipv4, ipv6, records };
}

function AddressList({ addresses }: { addresses: string[] }) {
  return (
    <>
      {addresses.map((address, idx) => (
        <span key={address}>
          {idx > 0 && ', '}
          <code>{address}</code>
        </span>
      ))}
    </>
  );
}

/**
 * Result of a DNS validation: status, the nodes' addresses, the A and AAAA
 * records found and what to fix. A records must point to a node's IPv4
 * address; AAAA records, if any, must all point to the nodes' IPv6 addresses
 * (otherwise IPv6 clients bypass the CDN).
 */
export function DnsValidationModal({ outcome, onClose }: DnsValidationModalProps) {
  const result = outcome?.result;
  const { ipv4, ipv6, records } = checkRecords(result);
  const expected = [...ipv4, ...ipv6];
  const aRecords = records.filter((record) => record.type === 'A');
  const aaaaRecords = records.filter((record) => record.type === 'AAAA');
  const aMismatch = !!result && !result.validated && aRecords.length > 0 && !aRecords.some((r) => r.matches);
  const aaaaMismatch = aaaaRecords.length > 0 && aaaaRecords.some((r) => !r.matches);

  return (
    <Modal
      isOpen={outcome !== null}
      onClose={onClose}
      size="sm"
      title={`DNS Validation: ${outcome?.domainName ?? ''}`}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {result && (
        <div className="domains-validation-content">
          <div
            className={`domains-validation-status ${result.validated ? 'status-success' : 'status-error'}`}
          >
            {result.validated ? 'Validated' : 'Validation Failed'}
          </div>

          {result.error && (
            <div className="domains-validation-error">
              <strong>Error:</strong> {result.error}
            </div>
          )}

          {expected.length > 0 && (
            <div className="domains-validation-row">
              <strong>{expected.length === 1 ? 'Expected IP (Node):' : 'Expected IPs (Nodes):'}</strong>
              <div className="domains-validation-records">
                {expected.map((address) => (
                  <code key={address}>{address}</code>
                ))}
              </div>
            </div>
          )}

          {records.length > 0 && (
            <div className="domains-validation-row">
              <strong>DNS Records:</strong>
              <div className="domains-validation-records">
                {records.map((record) => (
                  <code key={record.value} className={record.matches ? 'record-match' : 'record-mismatch'}>
                    {record.type} {record.value}
                    {record.matches && ' (match)'}
                  </code>
                ))}
              </div>
            </div>
          )}

          {aMismatch && (
            <div className="domains-validation-hint">
              {ipv4.length > 0 ? (
                <>
                  The A records do not point to the CDN nodes. Update the DNS A record to point to{' '}
                  <AddressList addresses={ipv4} />
                  {ipv6.length > 0 && (
                    <>
                      {' '}
                      and the AAAA record to <AddressList addresses={ipv6} />
                    </>
                  )}
                  .
                </>
              ) : (
                <>
                  The CDN nodes have no IPv4 address: remove the A records pointing elsewhere and point the
                  AAAA record to <AddressList addresses={ipv6} />.
                </>
              )}
            </div>
          )}

          {aaaaMismatch && (
            <div className="domains-validation-hint">
              {ipv6.length > 0 ? (
                <>
                  AAAA records point outside the CDN, so IPv6 clients bypass it. Point the AAAA records to{' '}
                  <AddressList addresses={ipv6} /> or remove them.
                </>
              ) : (
                <>
                  The CDN nodes have no IPv6 address, so the AAAA records let IPv6 clients bypass the CDN.
                  Remove the AAAA records or add the IPv6 addresses of the nodes.
                </>
              )}
            </div>
          )}

          {result.warnings?.map((warning) => (
            <div key={warning} className="domains-validation-hint" role="note">
              {warning}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
